import React from "react"
import {
	REASON_MESSAGE_TO_TOOLTIP,
	ApplicationStatus,
	ApproveMethodType,
	DecisionByEnum,
	EntityDeclineReason,
} from "../../resources/application"
import {
	ProductApplicationStatus,
	DecisionMethodType as ProductDecisionMethodType,
} from "../../resources/productApplication"
import HorizontalField, {optionalHorizontalField} from "../HorizontalField/HorizontalField"
import Card from "../../containers/Card/Card"
import {RiskRateField} from "../RiskRateField/RiskRateField"
import {RiskRate} from "../../resources/common"
import moment from "moment"
import Chips from "../Chips/Chips"
import {GridContainer} from "../../containers/GridContainer/GridContainer"
import DecisionBy from "./DecisionBy"
import EvaluationType from "./EvaluationType"

interface ApplicationDecisionProps {
	canViewDecision: boolean
	status: ApplicationStatus | ProductApplicationStatus
	decisionMethod?: ApproveMethodType | ProductDecisionMethodType
	decisionDateTime?: Date
	decisionReason?: string
	decisionUserId?: string
	decisionBy?: DecisionByEnum
	riskRate?: RiskRate
	decisionReasonCodes?: EntityDeclineReason[]
	showEvaluationType?: boolean
	archived?: boolean
	fieldClassName?: string
}

function isDecisionCardVisible(
	status: ApplicationStatus | ProductApplicationStatus,
	canViewDecision: boolean
): boolean {
	const isTerminalStatus = status === ApplicationStatus.Approved || status === ApplicationStatus.Denied
	const isDenied = status === ApplicationStatus.Denied
	return isTerminalStatus && (isDenied || canViewDecision)
}

export function ApplicationDecision({
	canViewDecision,
	status,
	decisionMethod,
	decisionDateTime,
	decisionReason,
	decisionUserId,
	decisionBy,
	riskRate,
	decisionReasonCodes,
	showEvaluationType,
	archived,
	fieldClassName,
}: ApplicationDecisionProps) {
	if (!isDecisionCardVisible(status, canViewDecision)) return <React.Fragment />

	return (
		<Card
			className="application-decision"
			title={status === ApplicationStatus.Approved ? "Approval Information" : "Denial Information"}
		>
			{canViewDecision && riskRate !== undefined && (
				<RiskRateField riskRate={riskRate} fieldClassName={fieldClassName} />
			)}
			{canViewDecision &&
				optionalHorizontalField("Method", decisionMethod, undefined, archived ? "archived-field" : "")}
			{decisionDateTime ? (
				<HorizontalField
					label={status === ApplicationStatus.Approved ? "Approved At" : "Denied At"}
					fieldClassName={fieldClassName}
				>
					<input type="text" readOnly value={moment(decisionDateTime).format("L LT")} className="input is-static" />
				</HorizontalField>
			) : null}
			{canViewDecision && decisionReason ? (
				<HorizontalField label="Notes" fieldClassName={fieldClassName}>
					<textarea readOnly value={decisionReason} className="textarea is-static has-fixed-size" />
				</HorizontalField>
			) : null}
			{canViewDecision && (
				<DecisionBy
					decisionUserId={decisionUserId}
					decisionBy={decisionBy}
					decisionMethod={decisionMethod}
					status={status}
					fieldClassName={fieldClassName}
				/>
			)}
			{canViewDecision && showEvaluationType ? <EvaluationType fieldClassName={fieldClassName} /> : null}
			{decisionReasonCodes && decisionReasonCodes.length > 0 ? (
				<>
					<div className="horizontal-separator" />
					<div className="decision-title"> Reasons </div>
					<GridContainer gap="20px">
						{decisionReasonCodes.map((dr) => (
							<div key={dr.entity}>
								<div className="decision-entity-name">{dr.entity}</div>
								{dr.reasons && (
									<Chips
										labels={dr.reasons}
										tooltips={dr.reasons.map((r) => REASON_MESSAGE_TO_TOOLTIP.get(r.toLowerCase()))}
										tooltipKeyPrefix={dr.entity}
										readonly
									/>
								)}
							</div>
						))}
					</GridContainer>
				</>
			) : null}
		</Card>
	)
}
